import React, { Fragment, useState, useEffect, useRef } from 'react';
import Grid from "../../../Grid";
import Button from "../../../Button";
import { Controller, useForm } from 'react-hook-form';
import DatePicker from "react-datepicker";
import styles from "./ShippingForm.module.sass";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OrderPackage from '../../../../models/OrderPackage';
import { Cart, Order, Package } from '../../../../models';
import axios from 'axios';

const Form = ({ orderPackageId, isLink, closeModal, defaultValue }) => {
  const { register, handleSubmit, control, trigger, watch, setValue, unregister, formState: { errors, dirtyFields } } = useForm();
  const watchItemNumber = watch("order_package_id");
  const watchShippingLocation = watch("shipping_location");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderPackage, setOrderPackage] = useState();
  // Result of the Item Number lookup: null (nothing scanned / lookup failed),
  // { loading: true }, or the endpoint's { found, students } payload.
  const [scanInfo, setScanInfo] = useState(null);
  const scanRequestRef = useRef(0);
  // Parts the shipper UNTICKED in "In this envelope" -- left out, still owed
  // (#5818). Keys come from the lookup's `parts`; reset with every new lookup.
  const [leftOut, setLeftOut] = useState([]);
  // Which print action (if any) the clicked submit button requested. Set in the
  // button's onClick (fires before submit), read once in onSubmit.
  const printActionRef = useRef(null);
  const isWorkOrder = (watchItemNumber || '').toString().startsWith('WO-');
  // A part shipped after its package, on its own label (#5818).
  const isOwedItem = (watchItemNumber || '').toString().toUpperCase().startsWith('OWE-');

  // A work order is a correction going back to the customer, and shipping one
  // emails them -- except with "Completed", which closes it silently. So a WO-
  // scan starts on Mailed rather than the item scan's "Completed" default. Only
  // while the operator has not chosen an action themselves -- a Completed picked
  // on purpose is left alone.
  // An owed part's label is a mailing label too, so it starts on Mailed.
  useEffect(() => {
    if ((isWorkOrder || isOwedItem) && !dirtyFields.shipping_location) {
      setValue('shipping_location', 'ship_to_mail');
    }
  }, [isWorkOrder, isOwedItem]);

  useEffect(() => {
    // Set default value for item number if provided
    if (defaultValue) {
      setValue('order_package_id', defaultValue);
    }

    if (!orderPackageId) return;

    OrderPackage
      .find(orderPackageId)
      .then((response) => {
        setOrderPackage(response.data)
      });

    setValue('order_package_id', orderPackageId);
  }, [orderPackageId, defaultValue]);

  // Look the scanned Item Number up so the operator can see WHO the envelope is for
  // -- Student / Teacher / Grade -- and pull the matching class composite before
  // shipping. Debounced because a barcode scanner types the value character by
  // character, and sequenced so a slow early response can't overwrite a later one.
  useEffect(() => {
    const scanned = (watchItemNumber || '').toString().trim();

    // Bumped BEFORE the empty-field early return, not after it. Submitting clears
    // the field, so a slow lookup for the envelope just shipped would otherwise
    // still be considered current and would repopulate the panel with that student
    // while the operator is already scanning the next one.
    const requestId = ++scanRequestRef.current;

    setLeftOut([]);

    if (!scanned) {
      setScanInfo(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      setScanInfo({ loading: true });

      axios
        .get('/api/v1/orders/scan_lookup', { params: { id: scanned } })
        .then((res) => {
          if (requestId !== scanRequestRef.current) return;
          setScanInfo(res.data);
        })
        .catch(() => {
          if (requestId !== scanRequestRef.current) return;
          setScanInfo(null);
        });
    }, 350);

    return () => clearTimeout(timer);
  }, [watchItemNumber])

  // Open the relevant work-order document in a new window; it auto-prints on load.
  const triggerPrint = (itemNumber, printAction) => {
    if (!printAction || !itemNumber.startsWith('WO-')) return;
    const woId = itemNumber.replace('WO-', '');
    const url = printAction === 'label'
      ? `/admin/work_orders/${woId}/packing_slip?label=1`
      : `/admin/work_orders/${woId}/packing_slip`;
    window.open(url, '_blank');
  }

  const onSubmit = (data) => {
    const printAction = printActionRef.current;
    printActionRef.current = null;
    const itemNumber = data.order_package_id;
    const date  = new Date();

    // Determine the ID format for the API:
    // - IDs with dashes (WO-xxx, ORDER-xxx, ITEM-xxx) are sent as-is
    // - Numeric IDs are Order IDs (printed on yearbook barcodes)
    const itemId = data.order_package_id.includes('-')
      ? data.order_package_id
      : `ORDER-${data.order_package_id}`;

    axios
      .post(`/api/v1/orders/mark_as_shipped`,
        { item:
            { shipper_initials: data.shipper_initials,
              ship_destination: data.shipping_location,
              // The moment of the scan, not just its date. A bare "9/28/2026" is
              // stored as 00:00 UTC -- 5pm Pacific the day before -- which put
              // shipments on the wrong day of /admin/package_production.
              ship_date: data.shipping_date || date,
              id: itemId,
              category: data.scan_category || 'photo',
              ups_tracking_number: data.ups_tracking_number || null,
              notes: data.notes || null,
              // Only for a shipment: a cancel or a work order owes nothing.
              owed: (isWorkOrder || data.shipping_location === 'cancelled') ? [] : leftOut,
            }
        })
      .then(() => {
        // Cancelled is the alternative to shipping: nothing to print for it.
        if (data.shipping_location === 'cancelled') {
          sendToastMessage(itemNumber, 'cancelled');
          // One cancel per choice: back to the page's default action, so the
          // next scan can't cancel an order that was meant to ship.
          setValue('shipping_location', 'completed');
          return;
        }
        sendToastMessage(itemNumber, leftOut.length > 0
          ? `marked as shipped -- ${leftOut.length} item(s) still owed`
          : 'marked as shipped');
        triggerPrint(itemNumber, printAction);
      })
      .catch((error) => {
        sendFailToastMessage(error, itemNumber)
      })
      .finally(() => {
        setValue('order_package_id', '');
      });
  }

  const sendToastMessage = (item_id, what) => {
    toast.success(`Item #${item_id} successfully ${what}`, {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }

  const sendFailToastMessage = (error, item_id) => {
    const message = error && error.response && error.response.data.errors || `Item #${item_id} was not marked as shipped. Please try again or check the item number`
    toast.error(message , {
      position: "bottom-left",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }

  return (
    <Fragment>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <Grid container justify='center' spacing={2}>
          <Grid item xs={3} md={3}>
            <label className={styles.label}>Initials</label>
            <input
              className={styles.input}
              {...register("shipper_initials", { required: true })}
            />
          </Grid>

          <Grid item xs={3} md={3}>
            <label className={styles.label}>Date</label>
            <Controller
              control={control}
              name="shipping_date"
              render={({ field }) => (
                <DatePicker selected={field.value || Date.now()}
                            showTimeSelect
                            onChange={(date) => field.onChange(date)}
                            className={styles.input} />
              )}
            />
          </Grid>

          <Grid item xs={3} md={3}>
            <label className={styles.label}>Action</label>
            <select {...register("shipping_location")} className={`${styles.input} ${styles.select}`}>
              <option value="completed">Completed</option>
              <option value="ship_to_mail">Mailed</option>
              <option value="ship_to_school">Ship to school</option>
              <option value="ship_ups">UPS</option>
              {/* Work orders only -- the server refuses it on an item/order scan,
                  whose ship_destination drives print routing (see WorkOrder). */}
              {isWorkOrder && <option value="digital_delivery">Digital Delivery</option>}
              {/* The alternative to shipping (Orders::Cancel): never stamps a ship
                  date, refunds nothing. Not for work orders. */}
              {!isWorkOrder && !isOwedItem && <option value="cancelled">Cancelled</option>}
            </select>
          </Grid>

          <Grid item xs={3} md={3}>
            <label className={styles.label}>Type</label>
            <select {...register("scan_category")} className={`${styles.input} ${styles.select}`} defaultValue="photo">
              <option value="photo">Photo Order</option>
              <option value="yearbook">Yearbook</option>
            </select>
          </Grid>
        </Grid>

        {watchShippingLocation === 'ship_ups' && (
          <div style={{ marginBottom: '10px' }}>
            <label className={styles.label}>UPS Tracking Number</label>
            <input
              className={styles.input}
              {...register("ups_tracking_number")}
              placeholder="Enter UPS tracking number"
            />
          </div>
        )}

        <label className={styles.label}>Item Number</label>
        <input
          className={styles.input}
          {...register("order_package_id", { required: true })}
        />

        {/* Who the scanned envelope is for. Shown so the operator can pull the matching
            class composite off the stack before shipping. */}
        {scanInfo && (
          <div className={styles.scanInfo}>
            {scanInfo.loading && <span className={styles.scanMuted}>Looking up…</span>}

            {!scanInfo.loading && (!scanInfo.found || scanInfo.students.length === 0) && (
              <span className={styles.scanMuted}>
                {`No student found for “${scanInfo.id}”`}
              </span>
            )}

            {!scanInfo.loading && scanInfo.found && scanInfo.students.length > 0 && (
              <table className={styles.scanTable}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Teacher</th>
                    <th>Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {scanInfo.students.map((student) => (
                    <tr key={student.student_id}>
                      <td>{student.name}</td>
                      <td>{student.teacher || '—'}</td>
                      <td>{student.grade || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* What may be left out of this envelope: the class picture, specialty
            items. Ticked = in the envelope; unticked = still owed, shipped later
            with its own label (#5818). */}
        {!isWorkOrder && watchShippingLocation !== 'cancelled' && scanInfo && !scanInfo.loading
          && scanInfo.parts && scanInfo.parts.length > 0 && (
          <div className={styles.scanInfo}>
            <div className={styles.label}>In this envelope (untick anything not included)</div>
            {scanInfo.parts.map((part) => (
              <div key={part.key}>
                <label style={{ fontWeight: 'normal' }}>
                  <input
                    type="checkbox"
                    checked={!leftOut.includes(part.key)}
                    onChange={(e) => setLeftOut((keys) => (e.target.checked
                      ? keys.filter((k) => k !== part.key)
                      : [...keys, part.key]))}
                  />
                  {` ${part.description}`}
                  {part.student ? ` — ${part.student}` : ''}
                  {leftOut.includes(part.key) ? ' (still owed)' : ''}
                </label>
              </div>
            ))}
          </div>
        )}

        <label className={styles.label}>Notes</label>
        <input
          className={styles.input}
          {...register("notes")}
          style={{ width: '100%' }}
        />

        {/* All actions share one row so the modal height matches the original
            single-Submit layout (no overflow past the modal's 80vh body). */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            classes={styles.submit}
            variant='submit'
            type='submit'
            style={{ flex: 1 }}
            disabled={!watchItemNumber || isSubmitting}
            onClick={() => { printActionRef.current = null; }}
          >
            Submit
          </Button>
          {isWorkOrder && (
            <>
              <Button
                classes={styles.submit}
                variant='submit'
                type='submit'
                style={{ flex: 1 }}
                disabled={!watchItemNumber || isSubmitting}
                onClick={() => { printActionRef.current = 'wo'; }}
              >
                Submit/Print WO
              </Button>
              <Button
                classes={styles.submit}
                variant='submit'
                type='submit'
                style={{ flex: 1 }}
                disabled={!watchItemNumber || isSubmitting}
                onClick={() => { printActionRef.current = 'label'; }}
              >
                Submit/Print Label
              </Button>
            </>
          )}
        </div>
      </form>
    </Fragment>
  )
}

export default Form;
